import React from "react";
import { Box, Stack, Typography } from "@mui/material";

import illustrationPurple from "assets/goals-empty-state/illustration_purple.svg";
import illustrationGrey from "assets/goals-empty-state/illustration_grey.svg";
import blobPurple1 from "assets/goals-empty-state/blob_purple_1.svg";
import blobPurple2 from "assets/goals-empty-state/blob_purple_2.svg";
import blobPurple3 from "assets/goals-empty-state/blob_purple_3.svg";
import blobGrey1 from "assets/goals-empty-state/blob_grey_1.svg";
import blobGrey2 from "assets/goals-empty-state/blob_grey_2.svg";

type EmptyStateVariant = "purple" | "grey";

export interface EmptyStateProps {
  /** Color treatment — purple for primary contexts, grey for secondary/nested. */
  variant?: EmptyStateVariant;
  /** Headline text. */
  title: string;
  /** Supporting copy below the title. */
  description: string;
  /** Action element (typically a Button). */
  action?: React.ReactNode;
  /** Whether the action renders above or below the text block. */
  actionPlacement?: "top" | "bottom";
  /** Min height of the empty state container. */
  minHeight?: number | string;
}

type BlobConfig = {
  src: string;
  /** Width / height ratio applied to the wrapper, in px-equivalent units that scale with the container. */
  width: string;
  height: string;
  /** Absolute positioning offsets. */
  top?: string;
  bottom?: string;
  left?: string;
  right?: string;
  rotate: number;
};

const BLOBS_BY_VARIANT: Record<EmptyStateVariant, BlobConfig[]> = {
  purple: [
    {
      src: blobPurple1,
      width: "1057px",
      height: "1072px",
      top: "-646px",
      left: "calc(50% - 579px)",
      rotate: 105,
    },
    {
      src: blobPurple2,
      width: "605px",
      height: "609px",
      bottom: "-460px",
      right: "-163px",
      rotate: 60,
    },
    {
      src: blobPurple3,
      width: "626px",
      height: "530px",
      bottom: "-400px",
      left: "-260px",
      rotate: -15,
    },
  ],
  grey: [
    {
      src: blobGrey1,
      width: "626px",
      height: "530px",
      bottom: "-335px",
      left: "-318px",
      rotate: -15,
    },
    {
      src: blobGrey2,
      width: "1183px",
      height: "1191px",
      top: "-842px",
      left: "calc(50% - 547px)",
      rotate: 120,
    },
  ],
};

const VARIANT_BORDER: Record<EmptyStateVariant, string> = {
  purple: "#F4ECFB",
  grey: "#EAECEF",
};

/**
 * Decorative empty-state container used across Goals & Targets screens.
 */
const EmptyState: React.FC<EmptyStateProps> = ({
  variant = "purple",
  title,
  description,
  action,
  actionPlacement = "top",
  minHeight = 360,
}) => {
  const blobs = BLOBS_BY_VARIANT[variant];
  const illustration = variant === "purple" ? illustrationPurple : illustrationGrey;

  const textBlock = (
    <Stack
      direction="column"
      alignItems="center"
      sx={{ width: "100%", textAlign: "center", wordBreak: "break-word" }}
    >
      <Typography
        component="p"
        sx={{
          fontWeight: 500,
          fontSize: "16px",
          lineHeight: 1.75,
          letterSpacing: "0.15px",
          color: "text.primary",
        }}
      >
        {title}
      </Typography>
      <Typography
        component="p"
        variant="body2"
        sx={{ color: "text.secondary", letterSpacing: "0.17px" }}
      >
        {description}
      </Typography>
    </Stack>
  );

  return (
    <Stack
      component="section"
      alignItems="center"
      justifyContent="center"
      aria-label={title}
      sx={{
        position: "relative",
        overflow: "hidden",
        width: "100%",
        minHeight,
        p: { xs: 4, md: 2 },
        bgcolor: "background.default",
        border: "1px solid",
        borderColor: VARIANT_BORDER[variant],
        borderRadius: "12px",
      }}
    >
      {/* Decorative blobs — hidden on small viewports to keep content clear */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          display: { xs: "none", md: "block" },
        }}
      >
        {blobs.map((blob, idx) => (
          <Box
            key={idx}
            sx={{
              position: "absolute",
              top: blob.top,
              bottom: blob.bottom,
              left: blob.left,
              right: blob.right,
              width: blob.width,
              height: blob.height,
              transform: `rotate(${blob.rotate}deg)`,
              transformOrigin: "center",
            }}
          >
            <Box
              component="img"
              src={blob.src}
              alt=""
              sx={{ display: "block", width: "85%", height: "85%" }}
            />
          </Box>
        ))}
      </Box>

      {/* Content */}
      <Stack
        direction="column"
        alignItems="center"
        gap={4}
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: 410,
          px: { xs: 3, md: 0 },
        }}
      >
        {actionPlacement === "top" && action}
        {textBlock}
        {actionPlacement === "bottom" && action}
      </Stack>

      {/* Illustration anchored bottom-right */}
      <Box
        component="img"
        src={illustration}
        alt=""
        aria-hidden
        sx={{
          pt: 8,
          position: { xs: "relative", md: "absolute" },
          bottom: 7,
          right: 7,
          width: { xs: 120, sm: 160, md: 170 },
          height: "auto",
          pointerEvents: "none",
        }}
      />
    </Stack>
  );
};

export default EmptyState;
